import { useTranslation } from "react-i18next";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { ButtonLink } from "@components/Button";
import { CMSImage } from "@components/CMSImage/CMSImage";
import { parseHtml } from "@components/HTMLParser/utils";
import { IconButton } from "@components/IconButton/IconButton";
import { CrossIcon } from "@components/icons";
import type { IAdvert } from "@services/adverts/types";
import { useRemoveBannerMessageMutation } from "@services/messages/mutations";

interface IJointBillAdvertProps {
  advert?: IAdvert;
}

export const JointBillAdvert: React.FC<IJointBillAdvertProps> = ({
  advert,
}) => {
  const { t } = useTranslation();

  const { mutateAsync: removeAdvert, isPending } =
    useRemoveBannerMessageMutation(advert?.bannerType ?? "");

  if (!advert) {
    return null;
  }

  const isAdvertLinkClientSide = advert?.link?.startsWith("/") ?? false;

  return (
    <JointBillTableRowLayout
      fullWidthBorderTop={true}
      mergeCenterCells={true}
      className="bg-[#FFE000]"
      style={{ backgroundColor: advert.billPageBackgroundColor }}
      nameCell={
        <div className="flex flex-row items-start gap-4 pr-4 md:items-center md:justify-between md:pr-6">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="h-[55px] w-[165px] flex-shrink-0">
              <CMSImage
                image={advert.billPageImage}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="w-full text-xl font-bold leading-6">
              {parseHtml(advert.billPageTitle ?? "")}
            </div>
          </div>
          <div className="flex h-full items-center">
            <IconButton
              icon={CrossIcon}
              onClick={() => removeAdvert()}
              aria-label={t("action.close")}
              isLoading={isPending}
              iconClassName="size-5"
            />
          </div>
        </div>
      }
      detailsOpenButtonTitle={t("adverts.actions.detailsOpenButton")}
      detailsCloseButtonTitle={t("adverts.actions.detailsCloseButton")}
      billDetails={
        <div
          key={advert.name}
          className="flex flex-col items-center justify-between gap-4 py-4 first:pt-0 last:pb-0 md:flex-row"
        >
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <div className="w-full pr-10 font-medium">
              {parseHtml(advert.billPageDescription ?? "")}
            </div>
            <ButtonLink
              href={advert.link}
              variant="secondary"
              target={isAdvertLinkClientSide ? "_self" : "_blank"}
              className="w-full min-w-[144px] md:w-fit"
            >
              {t("advert.actions.getOffer")}
            </ButtonLink>
          </div>
        </div>
      }
    />
  );
};
